import { apiFetch } from "@/lib/api";

import type { CorrectiveActionSource } from "./correctiveActionsApi";

export interface QualityEventNcrRow {
  id: number;
  issue: string | null;
  issueDate: string | null;
  type: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string | null;
  repairScrap: string | null;
  open: boolean;
  rcaId: number | null;
}

export interface QualityEventRca {
  id: number;
  status: string;
  problemStatement: string | null;
  createdAt: string;
  qualityCaseId: number | null;
}

export type QualityEventStatus = "open" | "closed";

export interface QualityEvent {
  id: number;
  createdAt: string;
  source: string | null;
  sourceCategory: CorrectiveActionSource | null;
  supplierId: number | null;
  supplierName: string | null;
  customerFsAccountId: number | null;
  customerName: string | null;
  status: QualityEventStatus;
  summary: string | null;
  qualityCaseId: number | null;
  qualityCaseTitle: string | null;
  qualityCaseStatus: string | null;
  ncrs: QualityEventNcrRow[];
  rcas: QualityEventRca[];
  freshdeskTicketIds: number[];
  rmas: { id: number; rmaNumber: string; status: string }[];
  correctiveActions: {
    id: number;
    carNumber: string | null;
    title: string | null;
    status: string;
    sourceType: string | null;
    openedAt: string | null;
  }[];
}

export interface QualityEventMessage {
  id: string;
  createdAt: string;
  authorUserId: string;
  authorEmail: string;
  authorDisplayName: string;
  body: string;
}

export interface QualityEventListResponse {
  rows: QualityEvent[];
  totalCount: number;
  limit: number;
  offset: number;
}

export function qualityEventLabel(id: number): string {
  return `Quality event ${id}`;
}

export function qualityEventStatusLabel(status: QualityEventStatus | null | undefined): string {
  return status === "closed" ? "Closed" : "Open";
}

export function qualityEventQueryKey(id: number) {
  return ["quality", "event", id] as const;
}

export function qualityEventMessagesQueryKey(id: number) {
  return ["quality", "event", id, "messages"] as const;
}

export interface QualityEventListFilters {
  search?: string;
  qualityCaseId?: number;
  status?: QualityEventStatus;
  /** Comma-separated source categories. Omit to include every source. */
  sourceCategory?: string;
  limit?: number;
  offset?: number;
}

function buildEventListQuery(filters: QualityEventListFilters): string {
  const params = new URLSearchParams();
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.qualityCaseId != null) params.set("qualityCaseId", String(filters.qualityCaseId));
  if (filters.status != null) params.set("status", filters.status);
  if (filters.sourceCategory != null) params.set("sourceCategory", filters.sourceCategory);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

export async function fetchQualityEvents(
  filters: QualityEventListFilters = {},
): Promise<QualityEventListResponse> {
  return apiFetch<QualityEventListResponse>(`/api/quality/events${buildEventListQuery(filters)}`);
}

export async function createQualityEvent(input: {
  sourceCategory: CorrectiveActionSource;
  summary?: string | null;
  supplierId?: number | null;
  customerFsAccountId?: number | null;
}): Promise<QualityEvent> {
  return apiFetch<QualityEvent>("/api/quality/events", {
    method: "POST",
    body: JSON.stringify({
      sourceCategory: input.sourceCategory,
      summary: input.summary ?? null,
      supplierId: input.supplierId ?? null,
      customerFsAccountId: input.customerFsAccountId ?? null,
    }),
  });
}

export async function fetchQualityEvent(id: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${id}`);
}

export async function updateQualityEventStatus(id: number, status: QualityEventStatus): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function fetchQualityEventMessages(eventId: number): Promise<{ messages: QualityEventMessage[] }> {
  return apiFetch<{ messages: QualityEventMessage[] }>(`/api/quality/events/${eventId}/messages`);
}

export async function addQualityEventMessage(
  eventId: number,
  body: string,
): Promise<{ messages: QualityEventMessage[] }> {
  return apiFetch<{ messages: QualityEventMessage[] }>(`/api/quality/events/${eventId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export async function linkQualityEventNcr(eventId: number, qualityIncidentId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/ncrs`, {
    method: "POST",
    body: JSON.stringify({ qualityIncidentId }),
  });
}

export async function createQualityEventNcr(eventId: number, issue: string): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/ncrs`, {
    method: "POST",
    body: JSON.stringify({ issue }),
  });
}

export async function linkQualityEventRma(eventId: number, rmaNumber: string): Promise<QualityEvent> {
  const trimmed = rmaNumber.trim();
  const asId = Number(trimmed);
  const body =
    Number.isFinite(asId) && String(asId) === trimmed ? { rmaId: asId } : { rmaNumber: trimmed };
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/rmas`, {
    method: "POST",
    body,
  });
}

export async function linkQualityEventCase(eventId: number, qualityCaseId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/case`, {
    method: "POST",
    body: JSON.stringify({ qualityCaseId }),
  });
}

export async function createQualityCaseForEvent(
  title: string,
  sourceType: CorrectiveActionSource | null,
): Promise<number> {
  const created = await apiFetch<{ case: { id: number } }>("/api/quality/cases", {
    method: "POST",
    body: JSON.stringify({ title, sourceType: sourceType ?? "other" }),
  });
  return created.case.id;
}

export async function addQualityEventFreshdeskTicket(eventId: number, freshdeskTicketId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/freshdesk-tickets`, {
    method: "POST",
    body: JSON.stringify({ freshdeskTicketId }),
  });
}

export async function removeQualityEventFreshdeskTicket(eventId: number, freshdeskTicketId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/freshdesk-tickets/${freshdeskTicketId}`, {
    method: "DELETE",
  });
}
